import debug from "debug";
import cascadeVault from "./CascadeVault";

const log = debug("app:mkprt");

/**
 * The `MakePartProperties` class is responsible for processing and managing part properties.
 * It handles different modes (e.g., `xm-part`, `user-cfg`) and ensures that part properties 
 * are correctly mapped and formatted.
 * @category Experience
 * @subcategory World
 */
export default class MakePartProperties {
  /**
   * Constructs an instance of `MakePartProperties`.
   * @param {object} experience - The experience instance containing data vault and event hub.
   */
  constructor(experience) {
    this.experience = experience;
  }

  cleanup() {
    try {
        this.experience = null;
        log("MakePartProperties cleanup completed.");
    } catch (error) {
        console.error("Error during MakePartProperties cleanup:", error);
    }
}

  /**
   * Processes properties based on the given category, part data, version, and figure data.
   * @param {string} category - The category of the part.
   * @param {object} part - The part data.
   * @param {string} version - The version of the part data.
   * @param {object} figure - The figure object containing part details.
   * @returns {object} - The processed properties object.
   */
  processProperties(category, part, version, figure) {
    log("category", category);
    log("part", part);
    log("version", version);
    log("figure", figure);
    const mode = figure.partData.mode;
    let props = {};

    if (mode === "xm-part" && part && part.props) {
      // xm-part mode: Use part.props directly without any changes
      props["Part Number"] = part.id;
      props["Manufacturer"] = part.mfgCode;
      props["Manufacturer Part Number"] = part.mfgPart;
      props["Form Factor"] = part.ffId;
      props["Price (USD)"] = part.price;

      // Merge additional properties from part.props if they exist
      if (part.props) {
        props = part.props.reduce((acc, prop) => {
          acc[`${prop.label}`] = prop.value;
          return acc;
        }, props);
      }

      log("properties - props (xm-part)", props);
      return props;
    } else {
      // Other modes: Use partData.props for labels and values
      const partData = figure.partData;
      log("this is partData for cfg-user", partData);
      let formFactorId;

      if (version === "1.0") {
        const formFactorIndex = figure.partData.formFactor.index;
        const partCategory = figure.partData.category;
        formFactorId = this.findFfIdByCategoryAndIndex(
          cascadeVault,
          partCategory,
          formFactorIndex
        );
        const userData = partData.properties || {}; // Get user-provided data
        props = this.preparePropertiesV1(category, userData); // Get properties for version 1.0
        log("Properties mapped for version 1.0:", props);
      } else {
        props = this._prepareProperties(category); // Initialize for other versions
        log("Properties initialized for other versions:", props);
      }

      log("formFactorId:", formFactorId);
      log("partData.formFactor:", partData.formFactor);
      log(
        "partData.properties['Form Factor']:",
        partData.properties?.["Form Factor"]
      );
      log(
        "partData.properties['FormFactor']:",
        partData.properties?.["FormFactor"]
      );

      // Step 2: Populate static properties, preferring values in partData.properties
      props["Part Number"] =
        partData.properties?.["Part Number"] ||
        partData.fullpartnumber ||
        partData.partNumber ||
        "enter Part Number";
      props["Manufacturer"] =
        partData.properties?.Manufacturer ||
        partData.mfgCode ||
        "enter Manufacturer";
      props["Manufacturer Part Number"] =
        partData.properties?.["Manufacturer Part Number"] ||
        partData.mfgPart ||
        "enter Manufacturer PN";
      props["Form Factor"] =
        formFactorId ||
        partData.formFactor ||
        partData.properties?.["Form Factor"] ||
        partData.properties?.["FormFactor"] ||
        "enter Form Factor";
      props["Price (USD)"] =
        partData.properties?.["Price (USD)"] || partData.price || 0;

      // Step 3: Use partData.props for additional labels and values with units

      Object.keys(props).forEach((label) => {
        if (
          (props[label] === null || props[label] === undefined) && // Only replace null/undefined properties
          partData.properties?.[label] !== undefined
        ) {
          props[label] = partData.properties[label];
        }
      });

      if (Array.isArray(partData.props)) {
        partData.props.forEach((prop) => {
          const labelWithUnit = prop.unit
            ? `${prop.label} (${prop.unit})`
            : prop.label;
          if (partData.properties?.[prop.label] !== undefined) {
            props[labelWithUnit] = partData.properties[prop.label];
          }
        });
      }

      log("partData.formFactpors", partData.formFactor);
      log("properties - props (other modes)", props);
      return props;
    }
  }

  /**
   * Finds the Form Factor ID by category and index.
   * @param {object} formFactors - The form factors data structure.
   * @param {string} category - The category of the form factor.
   * @param {number} targetIndex - The index of the form factor to find.
   * @returns {string|null} - The corresponding form factor ID or `null` if not found.
   */
  findFfIdByCategoryAndIndex(formFactors, category, targetIndex) {
    const formFactorArray = formFactors[category];

    if (formFactorArray) {
      for (const formFactor of formFactorArray) {
        if (formFactor.index === targetIndex) {
          return formFactor.ffId;
        }
      }
    }

    return null; // Return null if no matching ffId is found
  }

  /**
   * Prepares an initial properties object for a given category.
   * @param {string} category - The category for which properties should be initialized.
   * @returns {object} - The initialized properties object.
   */
  _prepareProperties(category) {
    const categoryData = this.experience.dataVault.categorytables.get(category);
    if (!categoryData || !categoryData.headers) {
      log(`No headers found for category: ${category}`);
      return {};
    }

    const properties = {};
    categoryData.headers.forEach((header) => {
      const label = header.lbl;
      properties[label] = null; // or a default value of your choice
    });

    return properties;
  }

  /**
   * Prepares properties specifically for version `1.0`, using user data and category headers.
   * @param {string} category - The category of the part.
   * @param {object} userData - The user-provided properties.
   * @returns {object} - The formatted properties object for version `1.0`.
   */
  preparePropertiesV1(category, userData) {
    log("Preparing properties for version 1.0, category:", category, userData);

    // Retrieve headers for the category
    const categoryData = this.experience.dataVault.categorytables.get(category);
    log("Category table headers:", categoryData?.headers);

    if (!categoryData || !categoryData.headers) {
      log(`No headers found for category: ${category}`);
      return {};
    }

    const headersMap = categoryData.headers;
    const preparedProperties = {};

    // Process each header, normalizing keys during comparison
    headersMap.forEach((header) => {
      const oldKey = header.oldKey ? header.oldKey : null;
      const key = header.key ? header.key : null;
      const lbl = header.lbl ? header.lbl : null;

      log("oldKey, key, lbl, incoming key", oldKey, key, lbl, userData);

      // Normalize userData key comparison on-the-fly
      const matchedKey =
        (oldKey && userData[oldKey]) || (key && userData[key]) || userData[lbl];

      preparedProperties[lbl] = matchedKey || null;
      log(`Mapped ${lbl}: ${preparedProperties[lbl]}`);
    });

    log("Final prepared properties:", preparedProperties);
    return preparedProperties;
  }
}
